import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { authenticatedFetch } from '@/utils/api';

interface Collection {
  id: number;
  name: string;
  is_private: boolean;
}

interface AddPromptToCollectionModalProps {
  promptId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddPromptToCollectionModal({ promptId, onClose, onSuccess }: AddPromptToCollectionModalProps) {
  const { isLoggedIn } = useAuth();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [selectedCollections, setSelectedCollections] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCollections, setIsLoadingCollections] = useState(true);
  const [error, setError] = useState('');
  const [promptAlreadyInCollections, setPromptAlreadyInCollections] = useState<Set<number>>(new Set());

  // Fetch user's collections and check if prompt is already in any collection
  useEffect(() => {
    const fetchCollectionsAndCheckPrompt = async () => {
      if (!isLoggedIn) return;
      
      try {
        setIsLoadingCollections(true);
        
        // Fetch collections
        const collectionsResponse = await authenticatedFetch('/api/collections');
        if (!collectionsResponse.ok) {
          throw new Error('Failed to fetch collections');
        }
        const collectionsData = await collectionsResponse.json();
        const userCollections = collectionsData.data || [];
        setCollections(userCollections);

        // Check which collections already contain this prompt
        const collectionsWithPrompt = new Set<number>();
        for (const collection of userCollections) {
          try {
            const checkResponse = await authenticatedFetch(`/api/collections/${collection.id}/prompts`);
            if (checkResponse.ok) {
              const promptsData = await checkResponse.json();
              const prompts = promptsData.data || [];
              const hasPrompt = prompts.some((prompt: any) => prompt.id === parseInt(promptId));
              if (hasPrompt) {
                collectionsWithPrompt.add(collection.id);
              }
            }
          } catch (error) {
            console.error(`Error checking collection ${collection.id}:`, error);
          }
        }
        
        setPromptAlreadyInCollections(collectionsWithPrompt);
        setSelectedCollections(collectionsWithPrompt); // Pre-select collections where prompt already exists
      } catch (error) {
        setError('Failed to load collections');
      } finally {
        setIsLoadingCollections(false);
      }
    };

    fetchCollectionsAndCheckPrompt();
  }, [isLoggedIn, promptId]);

  const handleCollectionToggle = (collectionId: number) => {
    setSelectedCollections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(collectionId)) {
        newSet.delete(collectionId);
      } else {
        newSet.add(collectionId);
      }
      return newSet;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setError('');
    setIsLoading(true);

    try {
      // Determine which collections to add and which to remove
      const collectionsToAdd = Array.from(selectedCollections).filter(
        collectionId => !promptAlreadyInCollections.has(collectionId)
      );
      
      const collectionsToRemove = Array.from(promptAlreadyInCollections).filter(
        collectionId => !selectedCollections.has(collectionId)
      );

      // Add prompts to new collections
      if (collectionsToAdd.length > 0) {
        const addResponse = await authenticatedFetch(`/api/collections/add-prompt`, {
          method: 'POST',
          body: JSON.stringify({
            prompt_id: parseInt(promptId),
            collection_ids: collectionsToAdd
          }),
        });

        if (!addResponse.ok) {
          throw new Error('Failed to add prompt to collections');
        }
      }

      // Remove prompts from unchecked collections
      for (const collectionId of collectionsToRemove) {
        const removeResponse = await authenticatedFetch(`/api/collections/${collectionId}/remove-prompt/${promptId}`, {
          method: 'DELETE',
        });

        if (!removeResponse.ok) {
          console.error(`Failed to remove prompt from collection ${collectionId}`);
        }
      }

      onSuccess();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error updating prompt collections. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md transform transition-all">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Add Prompt to Collections</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isLoading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Modal Body */}
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            {isLoadingCollections ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-electric-blue"></div>
              </div>
            ) : collections.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">You don't have any collections yet.</p>
                <p className="text-sm text-gray-400">Create a collection first to organize your prompts.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  {promptAlreadyInCollections.size > 0 
                    ? "Select the collections where you want this prompt to remain. Uncheck collections to remove the prompt from them."
                    : "Select the collection(s) you want to add this prompt to."
                  }
                </p>
                
                <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                  {collections.map((collection) => (
                    <label key={collection.id} className="flex items-center space-x-3 p-2 rounded-md hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedCollections.has(collection.id)}
                        onChange={() => handleCollectionToggle(collection.id)}
                        className="h-4 w-4 text-brand-electric-blue border-gray-300 rounded focus:ring-brand-electric-blue"
                        disabled={isLoading}
                      />
                      <span className="text-gray-800">{collection.name}</span>
                      {collection.is_private && (
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full ml-auto">Private</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Error message */}
            {error && (
              <p className="text-red-600 text-sm mt-4">{error}</p>
            )}

            {/* Action buttons */}
            {collections.length > 0 && (
              <div className="flex justify-end space-x-3 pt-4">
                <button 
                  type="button"
                  onClick={onClose}
                  className="bg-gray-200 text-gray-800 font-semibold py-2 px-4 rounded-md hover:bg-gray-300 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="bg-brand-dark-blue text-white font-semibold py-2 px-4 rounded-md hover:bg-brand-electric-blue transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isLoading}
                >
                  {isLoading ? 'Updating...' : 'Update Collection Assignments'}
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}